import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { PenLine, FileText, AlertCircle, CheckCircle2, FileEdit, XCircle, Send, Eye } from "lucide-react";
import { getEsignDashboard } from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, formatDateTime, tenantHour } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";

const NAVY = "#1e3a8a";
const TEAL = "#0f766e";
const AMBER = "#f59e0b";
const RED = "#dc2626";
const LIME = "#4d7c0f";

const EVENT_LABELS: Record<string, string> = {
  sent: "Sent to", viewed: "Viewed by", signed: "Signed by", declined: "Declined by",
};

function eventColor(type: string) {
  if (type === "signed") return "#16a34a";
  if (type === "declined") return RED;
  if (type === "viewed") return AMBER;
  return NAVY;
}

function eventIcon(type: string) {
  if (type === "signed") return <CheckCircle2 size={14} />;
  if (type === "declined") return <XCircle size={14} />;
  if (type === "viewed") return <Eye size={14} />;
  return <Send size={14} />;
}

const card: React.CSSProperties = {
  background: "white", borderRadius: 10, padding: "22px 24px",
  border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

// eSign module dashboard — role-aware: org view for admin/HR, a personal
// "waiting for you" slice for everyone else. One page, two payload shapes
// (spec: .claude/specs/module-dashboards-esign-performance.md).
export default function EsignDashboardPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["dashboard-esign"],
    queryFn: getEsignDashboard,
    refetchInterval: 60_000,
  });

  const firstName = (user?.name || "").split(" ")[0];
  const hour = tenantHour(tz);
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dayStr = formatDate(new Date(), "long", tz);

  const header = (
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
        {greeting}, {firstName} 👋
      </h1>
      <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
        {dayStr} · {data?.view === "personal" ? "Your documents" : "Signature requests at a glance"}
      </p>
    </div>
  );

  // ── Personal shape ──
  if (data?.view === "personal") {
    const waiting: any[] = data.waiting_for_you ?? [];
    const signed: any[] = data.recently_signed ?? [];
    return (
      <div style={{ maxWidth: 900 }}>
        {header}
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Waiting for You</div>
          {waiting.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 0", color: "#64748b", fontSize: 13 }}>
              Nothing waiting for your signature 🎉
            </div>
          ) : (
            waiting.map((w, i) => (
              <div key={w.recipient_id} role="button" tabIndex={0}
                onClick={() => navigate("/esign")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/esign"); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                  padding: "10px 0", borderBottom: i < waiting.length - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: `${AMBER}18`, color: AMBER,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PenLine size={14} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {w.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                    {w.sent_at ? `Sent ${formatDate(new Date(w.sent_at), "short", tz)}` : ""}
                    {w.expires_at ? ` · expires ${formatDate(new Date(w.expires_at), "short", tz)}` : ""}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Recently Signed by You</div>
          {signed.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#64748b", fontSize: 13 }}>No signed documents yet</div>
          ) : (
            signed.map((s, i) => (
              <div key={s.request_id + "-" + i} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 0", borderBottom: i < signed.length - 1 ? "1px solid #f1f5f9" : "none",
              }}>
                <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0, flex: 1, fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {s.title}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", flexShrink: 0 }}>
                  {s.signed_at ? formatDate(new Date(s.signed_at), "short", tz) : ""}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <QuickAction label="My Documents" icon={<FileText size={14} />} to="/esign" color={LIME} />
        </div>
      </div>
    );
  }

  // ── Org shape ──
  const stats = data?.stats;
  const outstanding: any[] = data?.outstanding ?? [];
  const trend: any[] = data?.completions_trend ?? [];
  const hasTrend = trend.some((m) => m.completed > 0);
  const activity: any[] = data?.activity ?? [];

  return (
    <div>
      {/* No width cap: the module's own list pages (Candidates, Jobs, Offers,
          Interview Hub) all run the full content width, so capping the module
          landing page made the layout jump as you moved between them. */}
      {header}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }} className="stat-cards-row">
        <StatCard label="Awaiting Signature" value={stats?.awaiting_signature ?? 0} color={NAVY}
          icon={<PenLine size={18} />} to="/esign" subtitle="Sent, not yet completed" />
        <StatCard label="Expiring Soon" value={stats?.expiring_soon ?? 0}
          color={(stats?.expiring_soon ?? 0) > 0 ? RED : TEAL}
          icon={<AlertCircle size={18} />} to="/esign"
          subtitle={(stats?.expiring_soon ?? 0) > 0 ? "Within 7 days" : "None at risk"} />
        <StatCard label="Completed This Month" value={stats?.completed_this_month ?? 0} color={TEAL}
          icon={<CheckCircle2 size={18} />} to="/esign" subtitle="Fully signed" />
        <StatCard label="Drafts" value={stats?.drafts ?? 0} color={AMBER}
          icon={<FileEdit size={18} />} to="/esign" subtitle="Not yet sent" />
        <StatCard label="Declined This Month" value={stats?.declined_this_month ?? 0} color={RED}
          icon={<XCircle size={18} />} to="/esign" subtitle="Needs follow-up" />
      </div>

      <div style={{ ...card, padding: "20px 24px", marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="Send for Signature" icon={<PenLine size={14} />} to="/esign" color={LIME} />
          <QuickAction label="HR Letters" icon={<FileText size={14} />} to="/letters" color={NAVY} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }} className="grid-2col">
        <div style={card}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Outstanding Requests</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Soonest expiry first</div>
          </div>
          {outstanding.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>Nothing outstanding</div>
          ) : (
            outstanding.map((r, i) => (
              <div key={r.request_id} role="button" tabIndex={0}
                onClick={() => navigate("/esign")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/esign"); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                  padding: "10px 0", borderBottom: i < outstanding.length - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                    {r.sent_at ? `Sent ${formatDate(new Date(r.sent_at), "short", tz)}` : "Not sent"}
                    {r.expires_at ? ` · expires ${formatDate(new Date(r.expires_at), "short", tz)}` : ""}
                  </div>
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: AMBER, background: `${AMBER}18`, padding: "3px 8px", borderRadius: 999, flexShrink: 0 }}>
                  {r.pending_signers} pending
                </span>
              </div>
            ))
          )}
        </div>

        <div style={card}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Completions</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Fully signed requests · last 6 months</div>
          </div>
          {!hasTrend ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 13 }}>No completed requests yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={trend} barSize={26}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
                <Tooltip cursor={{ fill: "#f8fafc" }} formatter={(v: number) => [v, "Completed"]} />
                <Bar dataKey="completed" name="Completed" fill={LIME} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Recent Activity</div>
        {activity.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#64748b", fontSize: 13 }}>No recent activity</div>
        ) : (
          activity.slice(0, 7).map((e, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "9px 0", borderBottom: i < Math.min(activity.length, 7) - 1 ? "1px solid #f1f5f9" : "none",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 1,
                background: `${eventColor(e.event_type)}15`, color: eventColor(e.event_type),
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {eventIcon(e.event_type)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {EVENT_LABELS[e.event_type] || e.event_type} {e.who} — {e.title}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                  {e.ts ? formatDateTime(new Date(e.ts), tz) : ""}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
